import { createFileRoute } from '@tanstack/react-router';
import {
    addToast,
    Button,
    Card,
    CardBody,
    CardHeader,
    Chip,
    Divider,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
} from '@heroui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCallback, useState } from 'react';
import TextInput from '@/lib/common/inputs/TextInput';
import { InferType, object, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import myAxios from '@/machinery/ax';

export const Route = createFileRoute('/ambassador-program')({
    component: RouteComponent,
});

const WHY_AMBASSADOR = [
    'Earn 20% commission on every sale generated for the lifetime of the subscription.',
    'Offer your audience an exclusive 15% discount code.',
    'All ambassadors receive X% off their personal/team subscription.',
    'Build recurring income from subscriptions.',
    'Perfect for coaches, players, and golf influencers.',
];

const INDIVIDUAL_EARNINGS = [
    {
        plan: 'Individual Advanced Caddy (Monthly)',
        price: '$15/mo',
        discount: '$12.75/mo',
        earnings: '$2.55/mo',
        referrals5: '$12.75/mo',
        referrals10: '$25.50/mo',
        referrals20: '$51.00/mo',
    },
    {
        plan: 'Individual Advanced Caddy (Yearly)',
        price: '$150/yr',
        discount: '$85/yr',
        earnings: '$17/yr',
        referrals5: '$85/yr',
        referrals10: '$170/yr',
        referrals20: '$340/yr',
    },
];

const COACH_EARNINGS = [
    {
        plan: 'Basic Caddy Team',
        price: '$300/yr',
        discount: '$255/yr',
        earnings: '$51/yr',
        referrals5: '$255/yr',
        referrals10: '$510/yr',
        referrals20: '$1,020/yr',
    },
    {
        plan: 'Advanced Caddy Team',
        price: '$500/yr',
        discount: '$425/yr',
        earnings: '$85/yr',
        referrals5: '$425/yr',
        referrals10: '$850/yr',
        referrals20: '$1,700/yr',
    },
    {
        plan: 'Master Caddy Team',
        price: '$1,000/yr',
        discount: '$850/yr',
        earnings: '$170/yr',
        referrals5: '$340',
        referrals10: '$1,700',
        referrals20: '$3,400',
    },
];

const schema = object().shape({
    email: string().required().email().label('Email'),
    name: string().required().label('Name'),
    company_or_school: string().required().label('Company or School'),
});

type AmbassadorRequestForm = InferType<typeof schema>;

function RouteComponent() {
    const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
    const f = useForm<AmbassadorRequestForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            email: '',
            name: '',
            company_or_school: '',
        },
    });

    const mutation = useMutation({
        mutationFn: async (values: AmbassadorRequestForm) => {
            await myAxios({
                method: 'post',
                url: 'marketing/ambassador-request',
                data: values,
            });
        },
        onError: () => {
            addToast({
                title: 'Failed to submit request, please try again',
                color: 'danger',
            });
        },
        onSuccess: () => {
            setIsApplyModalOpen(false);
            addToast({
                title: 'Request submitted successfully',
                description: 'We will get back to you soon',
                color: 'success',
            });
        },
    });

    const onSubmit = useCallback(
        async (values: AmbassadorRequestForm) => {
            mutation.mutate(values);
        },
        [mutation],
    );

    return (
        <main className='mx-auto mb-20 max-w-7xl px-4 py-10 md:px-8'>
            <section className='bg-linear-to-r mb-8 rounded-3xl from-green-500 to-teal-500 p-[2px]'>
                <Card className='rounded-[22px]'>
                    <CardBody className='gap-6 px-6 py-10 text-center md:px-12'>
                        <div className='mx-auto max-w-3xl'>
                            <Chip color='primary' variant='flat' className='mb-4'>
                                Partner Program
                            </Chip>
                            <h1 className='bg-linear-to-r mb-4 from-green-500 to-teal-500 bg-clip-text text-4xl font-bold tracking-tight text-transparent md:text-5xl'>
                                Caddy Stats Ambassador Program
                            </h1>
                            <p className='text-lg text-default-600'>
                                Join the Caddy Stats Ambassador Program and start earning while helping
                                golfers improve their game with data-driven insights.
                            </p>
                        </div>
                        <div className='mx-auto flex w-full max-w-md flex-col justify-center gap-3 sm:flex-row'>
                            <Button
                                color='primary'
                                size='lg'
                                onPress={() => setIsApplyModalOpen(true)}
                                className='font-semibold'>
                                Apply Now
                            </Button>
                            <Button
                                size='lg'
                                variant='bordered'
                                as='a'
                                href='#earnings'
                                className='font-semibold'>
                                View Earnings
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </section>

            <section className='mb-10'>
                <Card shadow='sm'>
                    <CardHeader className='px-6 pt-6'>
                        <h2 className='text-2xl font-semibold'>Why Become an Ambassador?</h2>
                    </CardHeader>
                    <CardBody className='pt-2'>
                        <ul className='list-disc space-y-3 pl-6 marker:text-primary'>
                            {WHY_AMBASSADOR.map(item => (
                                <li key={item} className='pr-2 text-base leading-relaxed text-default-700'>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </CardBody>
                </Card>
            </section>

            <section id='earnings' className='space-y-8'>
                <div>
                    <h2 className='text-2xl font-semibold'>Example Earnings</h2>
                    <p className='mt-1 text-default-500'>
                        Potential earnings based on 20% commission and a 15% audience discount.
                    </p>
                </div>

                <Card shadow='sm'>
                    <CardHeader className='px-6 pt-6'>
                        <h3 className='text-xl font-semibold'>Individual Ambassador</h3>
                    </CardHeader>
                    <CardBody>
                        <Table aria-label='Individual ambassador example earnings' removeWrapper>
                            <TableHeader>
                                <TableColumn>PLAN</TableColumn>
                                <TableColumn>PRICE</TableColumn>
                                <TableColumn>DISCOUNT 15%</TableColumn>
                                <TableColumn>YOUR 20% EARNINGS</TableColumn>
                                <TableColumn>X5 REFERRALS</TableColumn>
                                <TableColumn>X10 REFERRALS</TableColumn>
                                <TableColumn>X20 REFERRALS</TableColumn>
                            </TableHeader>
                            <TableBody items={INDIVIDUAL_EARNINGS}>
                                {item => (
                                    <TableRow key={item.plan}>
                                        <TableCell>{item.plan}</TableCell>
                                        <TableCell>{item.price}</TableCell>
                                        <TableCell>{item.discount}</TableCell>
                                        <TableCell>{item.earnings}</TableCell>
                                        <TableCell>{item.referrals5}</TableCell>
                                        <TableCell>{item.referrals10}</TableCell>
                                        <TableCell>{item.referrals20}</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardBody>
                </Card>

                <Card shadow='sm'>
                    <CardHeader className='px-6 pt-6'>
                        <h3 className='text-xl font-semibold'>Coach Ambassador</h3>
                    </CardHeader>
                    <CardBody>
                        <Table aria-label='Coach ambassador example earnings' removeWrapper>
                            <TableHeader>
                                <TableColumn>PLAN</TableColumn>
                                <TableColumn>PRICE</TableColumn>
                                <TableColumn>DISCOUNT 15%</TableColumn>
                                <TableColumn>YOUR 20% EARNINGS</TableColumn>
                                <TableColumn>X5 REFERRALS</TableColumn>
                                <TableColumn>X10 REFERRALS</TableColumn>
                                <TableColumn>X20 REFERRALS</TableColumn>
                            </TableHeader>
                            <TableBody items={COACH_EARNINGS}>
                                {item => (
                                    <TableRow key={item.plan}>
                                        <TableCell>{item.plan}</TableCell>
                                        <TableCell>{item.price}</TableCell>
                                        <TableCell>{item.discount}</TableCell>
                                        <TableCell>{item.earnings}</TableCell>
                                        <TableCell>{item.referrals5}</TableCell>
                                        <TableCell>{item.referrals10}</TableCell>
                                        <TableCell>{item.referrals20}</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardBody>
                </Card>
            </section>

            <Divider className='my-10' />

            <section>
                <Card className='bg-default-50' shadow='none'>
                    <CardBody className='flex flex-col items-center gap-3 py-8 text-center'>
                        <h3 className='text-2xl font-semibold'>Ready to get started?</h3>
                        <p className='max-w-2xl text-default-600'>
                            Reach out and we will help set up your custom discount code and ambassador
                            onboarding.
                        </p>
                        <Button
                            color='primary'
                            onPress={() => setIsApplyModalOpen(true)}
                            className='font-semibold'>
                            Contact Caddy Stats
                        </Button>
                    </CardBody>
                </Card>
            </section>

            <Modal isOpen={isApplyModalOpen} onOpenChange={setIsApplyModalOpen} placement='center'>
                <ModalContent>
                    <FormProvider {...f}>
                        <form onSubmit={f.handleSubmit(onSubmit)}>
                            <ModalHeader className='flex flex-col gap-1'>
                                Ambassador Program Contact Form
                            </ModalHeader>
                            <ModalBody className='gap-4'>
                                <TextInput name='email' label='Email' type='email' required placeholder=' ' />
                                <TextInput name='name' label='Name' required placeholder=' ' />
                                <TextInput
                                    name='company_or_school'
                                    label='Company or School'
                                    required
                                    placeholder=' '
                                />
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    variant='light'
                                    onPress={() => setIsApplyModalOpen(false)}
                                    isDisabled={mutation.isPending}>
                                    Cancel
                                </Button>
                                <Button color='primary' type='submit' isLoading={mutation.isPending}>
                                    Submit Request
                                </Button>
                            </ModalFooter>
                        </form>
                    </FormProvider>
                </ModalContent>
            </Modal>
        </main>
    );
}
